/**
 * Words and pure rules for the public signing page (SignPortal.jsx). Kept apart so jest pins them.
 * Plain words for a stranger's phone: no job numbers, no prices, no shop jargon.
 */

export const KINDS = Object.freeze({
  pickup: {
    title: 'Pickup receipt',
    listHeading: "You're taking",
    nameLabel: 'Your name',
    sigLabel: 'Sign with your finger',
    submit: () => 'Sign and finish',
    attestation: 'By signing you confirm you received the pallets listed above.',
    expiredHelp: 'Pickup codes last 15 minutes. Ask the person at the shop to show a new one.',
  },
  delivery: {
    title: 'Delivery receipt',
    listHeading: 'Pallets delivered',
    nameLabel: 'Received by (print name)',
    sigLabel: 'Receiver signs',
    submit: (problem) => (problem ? 'Confirm delivery with a problem noted' : 'Confirm delivery'),
    attestation: 'By signing you confirm you received the pallets listed above.',
    expiredHelp: 'Ask the driver for a new code, or call KanCraft.',
  },
  carrier_handoff: {
    title: 'Freight pickup receipt',
    listHeading: "Pallets you're taking",
    nameLabel: "Driver's name",
    sigLabel: 'Driver signs',
    submit: () => 'Sign and finish',
    attestation: 'By signing you confirm you picked up the pallets listed above.',
    expiredHelp: 'Ask the person at the shop to show a new code.',
  },
  // An outside company (a coater) signing for KanCraft's parts at its own dock (jobq-api 2026-10-29).
  vendor_receipt: {
    title: 'Parts received from KanCraft',
    listHeading: 'What KanCraft sent you',
    nameLabel: 'Received by (print name)',
    sigLabel: 'Sign to confirm you received it',
    submit: (problem) => (problem ? 'Sign with a problem noted' : 'Sign for receipt'),
    attestation: 'By signing you confirm you received the pallets and parts listed above from KanCraft, except any problem you noted.',
    expiredHelp: 'Ask KanCraft for a new code: call the shop or ask the person who brought the parts.',
  },
});

export function kindText(kind) {
  return KINDS[kind] || KINDS.pickup;
}

export const PROBLEM_KINDS = Object.freeze([
  { value: 'damaged', label: 'Damaged' },
  { value: 'short', label: 'Short' },
  { value: 'missing', label: 'Missing' },
  { value: 'other', label: 'Other' },
]);

/** "/sign/<token>" (or "/sign/<token>/", "?x") -> token. */
export function tokenFromPath(pathname) {
  const m = /^\/sign\/([^/?#]+)/.exec(String(pathname || ''));
  return m ? m[1] : '';
}

function plural(n, one, many) {
  return `${n} ${n === 1 ? one : many}`;
}

function num(n) {
  const v = Number(n);
  return Number.isFinite(v) ? v : null;
}

function fmtNum(n) {
  return Number(n).toLocaleString('en-US', { maximumFractionDigits: 1 });
}

/** "3 boxes · 30 pcs" (whatever of it the API gave). */
export function palletSummary(p) {
  const bits = [];
  const boxes = num(p && p.boxes);
  if (boxes) bits.push(plural(boxes, 'box', 'boxes'));
  const pcs = num(p && p.pieces);
  if (pcs !== null) bits.push(`${fmtNum(pcs)} pcs`);
  return bits.join(' · ');
}

/** "2 pallets · 46 pcs · 1,120 lb". */
export function totalsLine(totals, pallets) {
  const t = totals || {};
  const count = num(t.pallets) ?? (Array.isArray(pallets) ? pallets.length : null);
  const bits = [];
  if (count !== null) bits.push(plural(count, 'pallet', 'pallets'));
  const pcs = num(t.pieces);
  if (pcs !== null) bits.push(`${fmtNum(pcs)} pcs`);
  const lb = num(t.weight_lbs);
  if (lb) bits.push(`${fmtNum(Math.round(lb))} lb`);
  return bits.join(' · ');
}

export function lineCount(pallets) {
  return (Array.isArray(pallets) ? pallets : []).reduce((n, p) => n + (Array.isArray(p.lines) ? p.lines.length : 0), 0);
}

/** "Oct 27 at 2:14 PM" in the phone's own time zone. */
export function fmtWhen(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const day = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  return `${day} at ${time}`;
}

export function firstName(name) {
  const s = String(name || '').trim();
  if (!s) return '';
  const first = s.split(/\s+/)[0];
  // "R. Martinez" -> keep the whole name; an initial alone reads oddly.
  return /^[A-Za-z]\.?$/.test(first) ? s : first;
}

/** The customer's copy is served for 24 h after signing (the API answers 410 after that). */
export function receiptOffered(signedAt, now = Date.now()) {
  const t = new Date(signedAt || '').getTime();
  if (!Number.isFinite(t)) return false;
  return now - t < 24 * 60 * 60 * 1000;
}

/** Subtitle: "SO-1145 · Van Becelaere Machine Works". */
export function orderLine(order) {
  const o = order || {};
  return [o.order_number, o.customer_name].filter(Boolean).join(' · ');
}

/** A vendor receipt's subtitle: "KC-SVC-2026-0025 · KanCraft MFG to Alexander MFG". */
export function vendorLine(manifest) {
  const m = manifest || {};
  const route = m.from_name && m.vendor_name ? `${m.from_name} to ${m.vendor_name}` : (m.vendor_name || '');
  return [m.number, route].filter(Boolean).join(' · ');
}

/** "Oct 23" for a plain date ("2026-10-23"), read as that calendar day (no time-zone shift). */
export function fmtDay(ymd) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(ymd || ''));
  if (!m) return '';
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/** "Coating · RAL 7035 · due back Oct 23". */
export function vendorMeta(manifest) {
  const m = manifest || {};
  const bits = [];
  if (m.process) bits.push(m.process);
  if (m.finish) bits.push(m.finish);
  if (m.due_back) bits.push(`due back ${fmtDay(m.due_back)}`);
  return bits.join(' · ');
}

/** A vendor line's quantity: "78" / "3 sets". */
export function lineQty(ln) {
  const q = num(ln && ln.qty);
  if (q === null) return '';
  return ln.unit === 'sets' ? `${fmtNum(q)} ${q === 1 ? 'set' : 'sets'}` : fmtNum(q);
}

/** "PO 4471 · driver Lane Sotta · Truck 2". */
export function metaLine(order, load) {
  const bits = [];
  if (order && order.customer_po) bits.push(`PO ${order.customer_po}`);
  if (load && load.driver_name) bits.push(`driver ${load.driver_name}`);
  if (load && load.vehicle) bits.push(load.vehicle);
  return bits.join(' · ');
}

/** The full-screen message for a page that can't be signed. */
export function finalScreen(state, { kind, signed } = {}) {
  const k = kindText(kind);
  switch (state) {
    case 'signed':
      // signed.method (a 409 already_signed on POST /sign): 'paper' = signed on paper at the shop; 'esign' = another
      // code. No method = this code (the GET's own `signed`).
      if (signed && signed.method === 'paper') {
        return {
          tone: 'ok',
          title: 'Already signed',
          body: `These pallets were signed for on paper at the shop${signed.at ? ` on ${fmtWhen(signed.at)}` : ''}. There's nothing more to do.`,
        };
      }
      if (signed && signed.method === 'esign' && signed.name) {
        return {
          tone: 'ok',
          title: 'Already signed',
          body: `These pallets were already signed for by ${signed.name}${signed.at ? ` on ${fmtWhen(signed.at)}` : ''}. There's nothing more to do.`,
        };
      }
      return {
        tone: 'ok',
        title: 'Already signed',
        body: signed && signed.name
          ? `This receipt was signed by ${signed.name}${signed.at ? ` on ${fmtWhen(signed.at)}` : ''}. There's nothing more to do.`
          : "This receipt has already been signed. There's nothing more to do.",
      };
    case 'expired':
      return { tone: 'warn', title: 'This code has expired', body: `Nothing was signed. ${k.expiredHelp}` };
    case 'void':
      return {
        tone: 'warn',
        title: 'This code was cancelled',
        body: 'Nothing was signed with it. The shop made a new code or is recording the signature another way. Ask the person who gave it to you.',
      };
    case 'changed':
      if (kind === 'vendor_receipt') {
        return {
          tone: 'warn',
          title: 'This paperwork changed',
          body: 'KanCraft changed this paperwork after the code was made, so nothing was signed. Ask KanCraft for a new code.',
        };
      }
      return {
        tone: 'warn',
        title: 'Something changed at the shop',
        body: 'The pallets on this receipt changed after the code was made, so nothing was signed. Ask for a new code.',
      };
    case 'notfound':
      return {
        tone: 'warn',
        title: "This link doesn't work",
        body: 'Check that you scanned the whole code, or ask the shop for a new one.',
      };
    case 'unavailable':
      return {
        tone: 'warn',
        title: "Signing here isn't available right now",
        body: 'Please sign the paper copy instead.',
      };
    case 'ratelimited':
      return { tone: 'warn', title: 'Too many tries', body: 'Wait a few minutes, then try again.', retry: true };
    case 'offline':
    default:
      return { tone: 'warn', title: "Can't reach KanCraft", body: 'Check your signal, then try again.', retry: true };
  }
}

/** Which screen a failed GET shows. */
export function loadErrorState(err) {
  const status = err && err.status;
  if (status === 404) return 'notfound';
  if (status === 410) return 'expired';
  if (status === 429) return 'ratelimited';
  if ((status === 503 && err.detail) || status === 501) return 'unavailable';
  return 'offline';
}

/** Which screen the GET's own status asks for (null = the form). */
export function statusState(data) {
  const s = data && data.status;
  if (s === 'signed' || s === 'expired' || s === 'void') return s;
  return null;
}

/** Check the form before sending. -> a sentence, or null when it can go. */
export function formProblem({ name, hasSignature, problems, lineProblems, photos }) {
  if (String(name || '').trim().length < 2) return 'Please print your name.';
  if (!hasSignature) return 'Please sign in the box.';
  const missing = Object.entries(problems || {}).find(([, v]) => v && !v.kind);
  if (missing) return 'Pick what is wrong with each pallet you marked.';
  const lineMissing = Object.entries(lineProblems || {}).find(([, v]) => v && !v.kind);
  if (lineMissing) return 'Pick what is wrong with each part you marked.';
  if ((photos || []).some((p) => p.status === 'uploading')) return 'Wait for the photos to finish uploading.';
  return null;
}

/** A vendor line problem's key: the pallet it is flagged on and the line ("472:701"; ":710" = not on a pallet). */
export function lineProblemKey(palletId, lineId) {
  return `${palletId === null || palletId === undefined ? '' : palletId}:${lineId}`;
}

/** The POST /sign body. */
export function buildSignBody({
  clientRequestId, name, signaturePng, ask, deliveredTo, problemNote, problems, lineProblems, pallets, photos, geo,
  device, embedded,
}) {
  const a = ask || {};
  const palletIds = new Set((pallets || []).map((p) => p.pallet_id).filter((id) => id !== null && id !== undefined));
  const palletProblems = Object.entries(problems || {})
    .filter(([id, v]) => v && v.kind && palletIds.has(Number(id)))
    .map(([id, v]) => ({ pallet_id: Number(id), kind: v.kind, note: String(v.note || '').trim() || null }));
  // a vendor receipt (ask.line_problems): a problem on one part line ON one pallet (a line can sit on two), keyed
  // lineProblemKey(pallet, line) and sent as {pallet_id (null: the "Not on a pallet" group), line_id, kind, note}
  if (a.line_problems) {
    const shown = new Set((pallets || []).flatMap((p) => (Array.isArray(p.lines) ? p.lines : [])
      .filter((ln) => ln.line_id).map((ln) => lineProblemKey(p.pallet_id, ln.line_id))));
    Object.entries(lineProblems || {})
      .filter(([key, v]) => v && v.kind && shown.has(key))
      .forEach(([key, v]) => {
        const [pid, lid] = key.split(':');
        palletProblems.push({
          pallet_id: pid === '' ? null : Number(pid), line_id: Number(lid), kind: v.kind,
          note: String(v.note || '').trim() || null,
        });
      });
  }
  const note = String(problemNote || '').trim();
  return {
    client_request_id: clientRequestId,
    signer_name: String(name || '').trim().slice(0, 100),
    signature_png: signaturePng,
    // a vendor's optional "received at" note travels as delivered_to (jobq-api stores it as the received note)
    delivered_to: (a.delivered_to || a.received_note) ? (String(deliveredTo || '').trim() || null) : null,
    problem_note: note || null,
    pallet_problems: palletProblems,
    photo_upload_ids: (photos || []).filter((p) => p.upload_id && p.status !== 'failed').map((p) => p.upload_id),
    geo: geo || null,
    device: device || null,
    signed_on: embedded ? 'operator_device' : 'phone',
  };
}
